import { PlayerVideoBridge, NATIVE_VIDEO_CHANNEL } from '@playcastdotio/PeerCore';
import { getNotifyMessages, makeNonSerializableStateStore, makeStateStore } from '@playcastdotio/StateStore';
import {
  MakeClientPeerConfig,
  MakeHostPeerConfig,
  PlaycastLocalClients,
  PlaycastLocalHosts,
  PlaycastPeerClient,
  PlaycastPeerHost,
  PlaycastPeerRemoteControlClient,
  PlaycastPeerRemoteControlHost,
  PlaycastHostPeerStatus,
  PlaycastRemotePeerClients,
  PlaycastClientPeerStatus,
} from './types';
import {
  log,
  generateIdForPeer,
  warn,
  debug,
  isAppleDevice,
  isMobileDevice,
  isFirefoxBrowser,
  captureWebrtcError,
  recordDisconnectReason,
} from './util';
import { PlaycastWebrtcPreset, PlaycastWebrtcPresetGroup, PlaycastWebrtcStats } from './typesMedia';
import { defaultQualityPresetStore, serviceConfigs, USE_UDP_ONLY } from './defaultConfigs';
import {
  generators,
  PeerMessage,
  PeerMessageType,
  PlaycastMessage,
  PlaycastMessagePeerClose,
  PlaycastMessagePeerHandshakeReply,
  PlaycastMessagePeerUpdateTrackMetadata,
  PlaycastMessageTarget,
  validators,
  websocket,
} from '@playcastdotio/Messaging';
import Peer from '@bowbee/peer-lite';
// import { createClientSignaller, createHostSignaller } from './signaller';
import { createRemotePeer } from './peerRemote';
import { parseStats } from './stats';
import {
  applyCodecPreferencesToPeer,
  applyLowLatencyReceiverHints,
  manageKeyframeInterval,
  transformSdp,
} from './presets';
import { createMessageChunker } from './messageChunker';
import { startMetrics, stopMetrics } from './sentryStats';
import type {
  PlaycastPeerClientType,
  PlaycastTrackSet,
  PlaycastPeerMediaMetadata,
  PlaycastPeerClientMetadata,
  PlaycastDefaultProfiles,
  PlaycastChannels,
  PlaycastPeerHostType,
} from '@playcastdotio/CoreTypes';

export const createRemoteControlClient = (config: MakeClientPeerConfig, clientPeerId: string): PlaycastPeerRemoteControlClient => {
  return createLocalClient(config, 'remoteControlClient', clientPeerId) as PlaycastPeerRemoteControlClient;
};
export const createClient = (config: MakeClientPeerConfig, clientPeerId: string): PlaycastPeerClient => {
  return createLocalClient(config, 'client', clientPeerId) as PlaycastPeerClient;
};

export const createRemoteControlHost = (config: MakeHostPeerConfig, remoteControlHostPeerId: string): PlaycastPeerRemoteControlHost => {
  return createLocalHost(config, 'remoteControlHost', remoteControlHostPeerId) as PlaycastPeerRemoteControlHost;
};
export const createHost = (config: MakeHostPeerConfig, hostPeerId: string): PlaycastPeerHost => {
  return createLocalHost(config, 'host', hostPeerId) as PlaycastPeerHost;
};

const createLocalClient = (config: MakeClientPeerConfig, type: PlaycastPeerClientType, peerId?: string): PlaycastLocalClients => {
  const status = makeStateStore<PlaycastClientPeerStatus>({ connectionState: 'signalling' });
  const defaultTracks = makeNonSerializableStateStore<PlaycastTrackSet>({});
  const qualityPresetStore = makeStateStore<PlaycastWebrtcPresetGroup>(defaultQualityPresetStore.currentState);
  const statistics = makeStateStore<PlaycastWebrtcStats>({} as PlaycastWebrtcStats);
  const outgoingTracks = makeNonSerializableStateStore<PlaycastTrackSet>({});
  const incomingTrackMetadata = makeStateStore<{ [trackId: string]: PlaycastPeerMediaMetadata }>({});
  const incomingTracks = makeNonSerializableStateStore<PlaycastTrackSet>({});
  const peerMetadata: PlaycastPeerClientMetadata = {
    ...config.additionalMetadata,
    peerId: peerId ?? '',
    type,
    isAppleDevice: config.additionalMetadata?.isAppleDevice ?? isAppleDevice(),
    isMobileDevice: config.additionalMetadata?.isMobileDevice ?? isMobileDevice(),
    isFirefoxBrowser: config.additionalMetadata?.isFirefoxBrowser ?? isFirefoxBrowser(),
    userAgent: config.additionalMetadata?.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
  };
  const rawPeer = new Peer({
    ...{ ...config.signalServerConfig, ...serviceConfigs.peerOptions },
    channelLabel: 'control',
    channelOptions: {
      ordered: true,
      maxRetransmits: 0,
    },
    batchCandidates: true,
    batchCandidatesTimeout: 1000,
    config: {
      ...serviceConfigs.peerOptions.config,
      ...(config.iceServers
        ? { iceServers: [config.iceServers] }
        : {
            iceServers: [
              {
                // fallback to google ICE servers
                urls: ['stun:stun.l.google.com:19302'],
              },
            ],
          }),
      iceTransportPolicy: config.forceTurn ? 'relay' : 'all',
    },
  });
  const messageChunker = createMessageChunker(rawPeer);
  // messageChunker.setDebug(true);
  let messageCallback: ((message: PlaycastMessage<PlaycastMessageTarget>) => void) | undefined;

  if (peerId === undefined) {
    peerId = generateIdForPeer(type);
  }
  startMetrics(peerId);

  rawPeer.on('status', (state) => {
    log('rawPeer.onStatus', state);
    if (state === 'disconnected' || state === 'closed' || state === 'failed') {
      try {
        recordDisconnectReason(`webrtc:raw_peer_status:ice_${state}`, `client:${peerId ?? ''}`);
      } catch (err) {
        console.warn('[Peer] Failed to record webrtc status', err);
      }
      status.updateState({ connectionState: 'closed' });
      nativeVideo.close();
    }
  });

  const statsCaptureInterval = setInterval(() => {
    const pc = nativeVideo.getVideoPeerConnection() ?? rawPeer.get();
    parseStats(pc, 'localclient_' + peerId).then((stats) => {
      statistics.updateState(stats);
    });
  }, 1000);

  const changeQualityProfile = (profile: PlaycastDefaultProfiles) => {
    const message = generators.peer.changeRequestedProfile('player', peerId ?? 'unknown', profile);
    rawPeer.send(JSON.stringify(message), 'control');
  };
  const setQualityPresetGroup = (presetGroup: PlaycastWebrtcPresetGroup) => {
    debug('setQualityPresetGroup', presetGroup);
    qualityPresetStore.updateState(presetGroup);
  };
  const setQualityPreset = (channel: PlaycastChannels, preset: Partial<PlaycastWebrtcPreset>) => {
    debug('setQualityPreset', channel, preset);
    const presetGroup = qualityPresetStore.currentState;
    const newPresetGroup = {
      ...presetGroup,
      [channel]: {
        ...presetGroup[channel],
        ...preset,
      },
    };
    qualityPresetStore.updateState(newPresetGroup);
  };
  const close = (kickReason?: string) => {
    debug('close');
    try {
      if (kickReason) recordDisconnectReason(kickReason, `client:${peerId ?? ''}`);
    } catch (err) {
      console.warn('[Peer] Failed to record client close reason', err);
    }
    manageKeyframeInterval.stopAllForPeer(peerId ?? '');
    // disconnect this peer
    rawPeer.send(JSON.stringify(generators.peer.close('player', peerId ?? 'unknown', kickReason)), 'control');
    nativeVideo.close();
    rawPeer.destroy();
    stopMetrics(peerId ?? '');
    messageChunker.cleanup();
    status.updateState({ connectionState: 'closed', kickReason });
    // clean up state stores too
    status.reset(true);
    defaultTracks.reset(true);
    qualityPresetStore.reset(true);
    statistics.reset(true);
    outgoingTracks.reset(true);
    incomingTrackMetadata.reset(true);
    incomingTracks.reset(true);
    // kill the stats capture interval
    clearInterval(statsCaptureInterval);
  };

  const sendMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
    messageChunker.sendMessage(message, 'messages');
  };
  const onMessage = (callback: (message: PlaycastMessage<PlaycastMessageTarget>) => void) => {
    debug('onMessage');
    messageCallback = callback;
  };
  const disableStats = () => {
    debug('disableStats');
    clearInterval(statsCaptureInterval);
    statistics.reset();
  };

  let targetId = '';
  const connect = async (targetHostId: string) => {
    targetId = targetHostId;
    log('connect', targetHostId);
    status.updateState({ connectionState: 'signalling' });
    status.updateState({ connectionState: 'validating' });
    rawPeer.init();
    nativeVideo.start();
    applyCodecPreferencesToPeer(rawPeer, qualityPresetStore.currentState, peerMetadata);
    // PRISM-style: ensure transceivers exist (audio first, then video) before negotiation on the offerer side
    try {
      const pc = rawPeer.get();
      const trs = pc.getTransceivers();
      const hasAudio = trs.some((t) => t.receiver?.track?.kind === 'audio' || (t.sender && (t.sender as any).track?.kind === 'audio'));
      const hasVideo = trs.some((t) => t.receiver?.track?.kind === 'video' || (t.sender && (t.sender as any).track?.kind === 'video'));
      if (!hasAudio) pc.addTransceiver('audio', { direction: 'sendrecv' });
      if (!hasVideo) pc.addTransceiver('video', { direction: 'sendrecv' });
      // After creating, apply codec prefs again now that transceivers exist
      applyCodecPreferencesToPeer(rawPeer, qualityPresetStore.currentState, peerMetadata);
    } catch (e) {
      // ignore if transceivers not supported
    }
    const resolvedPeerId = peerId as string;
    rawPeer.on('signal', (data) => {
      log('rawPeer.onSignal', data);
      if (!data.sdp) {
        return;
      }
      // const transformed = transformSdp(data, qualityPresetStore.currentState.default);
      console.log('Sending SDP', resolvedPeerId, targetHostId, data.type);
      config.realtimeApiManager.send(
        websocket.generators.peer(PeerMessageType.PEER_SDP_OFFER, {
          fromId: resolvedPeerId,
          toId: targetHostId,
          sdp: {
            sdp: data.sdp,
            type: data.type,
          },
        }),
      );
    });
    rawPeer.on('onicecandidates', (candidates) => {
      log('rawPeer.onIceCandidates', candidates);
      const filtered = USE_UDP_ONLY
        ? candidates.filter((c: RTCIceCandidate | string) => {
            try {
              const obj: any = typeof c === 'string' ? JSON.parse(c) : c;
              const proto = typeof obj.protocol === 'string' ? obj.protocol.toLowerCase() : null;
              if (proto) return proto === 'udp';
              const cand: string = obj.candidate ?? '';
              return cand.includes(' udp ');
            } catch {
              return true;
            }
          })
        : candidates;
      config.realtimeApiManager.send(
        websocket.generators.peer(PeerMessageType.PEER_ICE_CANDIDATE, {
          fromId: resolvedPeerId,
          toId: targetHostId,
          candidates: {
            json: filtered.map((candidate) => JSON.stringify(candidate)),
          },
        }),
      );
    });
    rawPeer.start({ polite: false });
    rawPeer.addDataChannel('messages', {
      ordered: true,
      maxRetransmits: 0,
    });
  };

  const onSignal = (sdp: RTCSessionDescriptionInit) => {
    debug('signaller.onSignal', sdp);
    rawPeer.signal(sdp);
    try {
      setTimeout(() => {
        const pc = rawPeer.get();
        if (!pc) return;
        const queue: RTCIceCandidate[] = (pc as any).__pendingRemoteIce || [];
        if (pc.remoteDescription && queue.length > 0) {
          (pc as any).__pendingRemoteIce = [];
          queue.forEach((c) => {
            try {
              rawPeer.addIceCandidate(c);
            } catch (e) {
              console.warn('[Peer] Failed to flush ICE candidate:', e);
            }
          });
        }
      }, 0);
    } catch {
      // ignore if transceivers not supported
    }
  };
  const onCandidate = (candidate: RTCIceCandidate) => {
    debug('signaller.onCandidate', candidate);
    rawPeer.addIceCandidate(candidate);
  };

  const handlePeerMessage = (message: PeerMessage) => {
    switch (message.type) {
      case PeerMessageType.PEER_SDP_ANSWER:
      case PeerMessageType.PEER_SDP_OFFER: {
        if (message.sdp?.type && message.sdp?.sdp) {
          const sdpObj: RTCSessionDescriptionInit = {
            type: message.sdp.type, // Ensure 'type' is present
            sdp: message.sdp.sdp, // Ensure 'sdp' is present
          };
          onSignal(sdpObj);
        } else {
          console.warn('Invalid SDP message:', message.sdp);
        }
        break;
      }
      case PeerMessageType.PEER_ICE_CANDIDATE: {
        message.candidates.json.forEach((candidate) => {
          const candidateObj: RTCIceCandidate = JSON.parse(candidate);
          onCandidate(candidateObj);
        });
        break;
      }
    }
  };

  qualityPresetStore.addListener((presetGroup) => {
    debug('qualityPresetStore.addListener', presetGroup);
    applyCodecPreferencesToPeer(rawPeer, presetGroup, peerMetadata);
  });

  const receivePlayerStream = (stream: MediaStream) => {
    debug('rawPeer.onStreamRemote', stream);
    debug('incomingtrackmetadata', incomingTrackMetadata.currentState);

    let wasChange = false;
    // check to see if we have metadata for this track
    const streamTracks = stream.getTracks();
    streamTracks.forEach((track) => {
      debug('rawPeer.onStreamRemote.track', track);
      const trackId = track.id;
      const metadata = incomingTrackMetadata.currentState[trackId];
      // if (metadata) {
      // disabling to test no metadata
      // eslint-disable-next-line no-constant-condition
      if (false) {
        // if (metadata.channel) {
        //   const preset = qualityPresetStore.currentState[metadata.channel] ?? qualityPresetStore.currentState.default;
        //   applyCodecPreferencesToPeer(rawPeer, qualityPresetStore.currentState, peerMetadata, metadata.channel);
        //   // Apply jitterBufferTarget if this is a video track
        //   if (track.kind === 'video' && preset.videoReceive?.jitterBufferTarget !== undefined) {
        //     const transceivers = rawPeer.get().getTransceivers();
        //     const transceiver = transceivers.find((t) => t.receiver.track === track);
        //     try {
        //       if (transceiver) {
        //         const target = preset?.videoReceive?.jitterBufferTarget;
        //         if (target !== undefined) {
        //           if (target !== undefined && target >= 0 && target <= 4000) {
        //             (transceiver?.receiver as any).jitterBufferTarget = target;
        //           } else {
        //             console.warn(`[Peer] Invalid jitterBufferTarget value: ${target}ms. Must be between 0-4000ms.`);
        //           }
        //         }
        //       }
        //     } catch (err) {
        //       console.warn('[Peer] Failed to set jitterBufferTarget:', err);
        //     }
        //   }
        // }
        // wasChange = true;
        // incomingTracks.currentState[trackId as PlaycastChannels] = {
        //   track,
        //   metadata,
        // };
        // if (metadata.type === 'video') {
        //   resetQualityStore('localclient_' + peerId);
        // }
      } else {
        wasChange = true;
        const defaultPreset = qualityPresetStore.currentState.default;
        incomingTracks.currentState[trackId as PlaycastChannels] = {
          track,
          metadata: {
            type: track.kind === 'video' ? 'video' : 'audio',
            channel: 'default',
            displayName: 'unknown',
          },
        };
        // Apply the low-latency receiver hints now that the real track is attached. This is
        // the only point on the guest where a receiver is known to be carrying media:
        // applyCodecPreferencesToPeer runs during connect, and skips every transceiver whose
        // receiver has no track yet.
        if (track.kind === 'video' && defaultPreset.videoReceive?.jitterBufferTarget !== undefined) {
          const transceivers = (nativeVideo.getVideoPeerConnection() ?? rawPeer.get()).getTransceivers();
          const transceiver = transceivers.find((t) => t.receiver.track === track);
          if (transceiver) {
            applyLowLatencyReceiverHints(transceiver.receiver, defaultPreset.videoReceive.jitterBufferTarget);
          }
        }
        warn('No metadata found for incoming track', trackId);
      }
    });
    // Remove any tracks that are no longer in the stream
    const trackIds = Object.keys(incomingTracks.currentState);
    const removedTracks: string[] = [];
    trackIds.forEach((trackId) => {
      const track = incomingTracks.currentState[trackId as PlaycastChannels]?.track;
      if (track && !streamTracks.includes(track)) {
        removedTracks.push(trackId);
      }
    });
    removedTracks.forEach((trackId) => {
      wasChange = true;
      // Remote tracks belong to their peer; keep Electron video available for fallback.
      delete incomingTracks.currentState[trackId as PlaycastChannels];
    });

    if (wasChange) incomingTracks.updateState(incomingTracks.currentState);
  };
  const nativeVideo = new PlayerVideoBridge(rawPeer, receivePlayerStream);

  let controlChannelLabel = '';

  let pendingMessages: { message: PlaycastMessage<PlaycastMessageTarget> }[] = [];
  const internalInitialSend = (message: PlaycastMessage<PlaycastMessageTarget>) => {
    if (controlChannelLabel === '') {
      log('controlChannelLabel is not set, adding to pending messages', message);
      pendingMessages.push({ message });
    } else {
      if (rawPeer.getDataChannel(controlChannelLabel)?.readyState === 'open') {
        log('Sending message', message);
        rawPeer.send(JSON.stringify(message), controlChannelLabel);
        rawPeer.addDataChannel('messages', {
          ordered: true,
          maxRetransmits: 0,
        });
      }
    }
  };

  rawPeer.on('channelOpen', (e) => {
    if (e.channel.label === NATIVE_VIDEO_CHANNEL) return;
    log('rawPeer.onChannelOpen', e);
    if (controlChannelLabel === '') {
      controlChannelLabel = e.channel.label;
      if (pendingMessages.length > 0) {
        log('Sending pending messages', pendingMessages);
        pendingMessages.forEach((pendingMessage) => {
          const success = rawPeer.send(JSON.stringify(pendingMessage.message), controlChannelLabel);
          log('Sending pending message', pendingMessage, success);
        });
        pendingMessages = [];
        if (rawPeer.getDataChannel('messages') === undefined) {
          rawPeer.addDataChannel('messages', {
            ordered: true,
            maxRetransmits: 0,
          });
        }
      }
    }
  });

  rawPeer.on('channelData', (data) => {
    // log('rawPeer.onChannelData', data);
    if (data.source === 'outgoing') {
      return;
    }
    if (data.channel.label === NATIVE_VIDEO_CHANNEL) return;
    const channelLabel = data.channel.label;
    const parsedMessage = messageChunker.handleIncomingMessage(data.data, data.channel.label);
    console.log('GOT MESSAGE', parsedMessage);
    if (parsedMessage) {
      const validMessage = validators.validatePlaycastMessage(parsedMessage);
      if (!validMessage) {
        log('Invalid message received', parsedMessage);
        return;
      }
      // log('channelLabel', channelLabel);
      switch (channelLabel) {
        case 'messages': {
          if (messageCallback) {
            messageCallback(validMessage);
          }
          // log('rawPeer.onChannelData.messages', message);
          break;
        }
        case 'control': {
          if (validMessage.header.target !== 'peer') {
            log('Invalid message target', validMessage.header.target);
            return;
          }
          if (validMessage.header.action === 'close') {
            log('Received close message from remote peer', targetId);
            const closeMessage = validMessage.body.message as PlaycastMessagePeerClose['message'];
            close(closeMessage.reason);
          }
          if (validMessage.header.action === 'updateTrackMetadata') {
            const trackMetadata = validMessage.body.message as PlaycastMessagePeerUpdateTrackMetadata['message'];
            // alert('updateTrackMetadata' + JSON.stringify(trackMetadata));
            debug('updateTrackMetadata', trackMetadata);
            incomingTrackMetadata.updateState(trackMetadata.metadata);
          }
          if (validMessage.header.action === 'handshake' && validMessage.header.isReply) {
            // We are the client, this is a reply.
            const handshakeMessage = validMessage.body.message as PlaycastMessagePeerHandshakeReply['message'];
            if (handshakeMessage.valid) {
              status.updateState({ connectionState: 'connected' });
            }
          }
          // log('rawPeer.onChannelData.control', message);
          break;
        }
      }
    }
  });

  const peer: PlaycastLocalClients = {
    peerId: peerId ?? '',
    status,
    qualityPresetStore,
    type,
    metadata: peerMetadata,
    statistics,
    outgoingTracks,
    incomingTracks,
    handlePeerMessage,
    changeQualityProfile,
    setQualityPreset,
    setQualityPresetGroup,
    close,
    sendMessage,
    onMessage,
    disableStats,
    connect,
  };

  incomingTracks.addListener((tracks) => {
    const filteredTracks = Object.values(tracks).map((track) => {
      return {
        type: track.metadata.type,
        channel: track.metadata.channel,
        displayName: track.metadata.displayName || 'none',
        trackId: track.track.id,
      };
    });
    if (filteredTracks.length === 0) {
      filteredTracks.push({
        type: 'No type',
        channel: 'No channel',
        displayName: 'No display name',
        trackId: 'No tracks',
      } as never);
    }
    log('Incoming tracks from remote peer', targetId);
    console.table(filteredTracks);
  });

  const statusInterval = setInterval(() => {
    // get the status of the peer
    const peerStatus = rawPeer.status();
    log('peerStatus', peerStatus);
    if (peerStatus === 'connected') {
      if (status.currentState.connectionState === 'validating') {
        if (!peerId) {
          throw new Error('peerId is not defined');
        }
        log('Handshaking with remote peer', targetId);
        const handshakeMessage = generators.peer.handshake('player', peerId, peerMetadata);
        internalInitialSend(handshakeMessage);
      } else {
        log('Peer connected');
        status.updateState({ connectionState: 'connected' });
      }
      clearInterval(statusInterval);
    }
    if (peerStatus === 'disconnected' || peerStatus === 'closed' || peerStatus === 'failed') {
      status.updateState({ connectionState: 'closed' });
      clearInterval(statusInterval);
    }
    if (peerStatus === 'completed') {
      status.updateState({ connectionState: 'closed' });
      clearInterval(statusInterval);
    }
  }, 200);
  // If the peer is still in signalling state after 20 seconds, close the connection
  setTimeout(() => {
    clearInterval(statusInterval);
    const currentState = status.currentState.connectionState;
    // Only log timeout if still in signalling/validating AND not already closed
    if (currentState === 'signalling' || currentState === 'validating') {
      warn('Peer connection timed out', currentState);
      captureWebrtcError(rawPeer.get(), 'Peer connection timed out');
      status.updateState({ connectionState: 'closed' });
      close();
    } else if (currentState === 'closed') {
      // Connection already closed by statusInterval, don't log timeout error
      log('Peer connection timeout check - already closed, skipping error log');
    }
  }, 20000);

  return peer;
};

const createLocalHost = (config: MakeHostPeerConfig, type: PlaycastPeerHostType, hostPeerId: string): PlaycastLocalHosts => {
  const status = makeStateStore<PlaycastHostPeerStatus>({ connectionState: 'signalling' });
  const remotePeers = makeNonSerializableStateStore<{ [peerId: string]: PlaycastRemotePeerClients }>({});
  const defaultTracks = makeNonSerializableStateStore<PlaycastTrackSet>({});
  const qualityPresetStore = makeStateStore<PlaycastWebrtcPresetGroup>(defaultQualityPresetStore.currentState, {
    replicate: {
      channel: 'hostQualityPresets',
      targets: ['player'],
    },
  });
  const statistics = makeStateStore<PlaycastWebrtcStats | null>(null);
  let shouldHandlePanics = true;

  const changeQualityProfile = (profile: PlaycastDefaultProfiles) => {
    debug('changeQualityProfile', profile);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.changeQualityProfile(profile);
    });
  };
  const setQualityPresetGroup = (presetGroup: PlaycastWebrtcPresetGroup) => {
    debug('setQualityPresetGroup', presetGroup);
    qualityPresetStore.updateState(presetGroup);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.applyQualityPresetGroup(presetGroup);
    });
  };
  const setQualityPreset = (channel: PlaycastChannels, preset: Partial<PlaycastWebrtcPreset>) => {
    debug('setQualityPreset', channel, preset);
    const presetGroup = qualityPresetStore.currentState;
    const newPresetGroup = {
      ...presetGroup,
      [channel]: {
        ...presetGroup[channel],
        ...preset,
      },
    };
    qualityPresetStore.updateState(newPresetGroup);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.applyQualityPreset(channel, preset);
    });
  };

  const broadcastMessage = (message: PlaycastMessage<PlaycastMessageTarget>) => {
    // debug('broadcastMessage', message);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.sendMessage(message);
    });
  };

  const addTracksForAll = (tracks: PlaycastTrackSet) => {
    debug('addTracksForAll', tracks);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.addTracks(tracks);
    });
  };
  const removeTracksForAll = (tracks: PlaycastTrackSet) => {
    debug('removeTracksForAll', tracks);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.removeTracks(tracks);
    });
  };
  const setTracksForAll = (tracks: PlaycastTrackSet) => {
    debug('setTracksForAll', tracks);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.setTracks(tracks);
    });
  };
  const setDefaultTracks = (tracks: PlaycastTrackSet) => {
    debug('setDefaultTracks', tracks);
    defaultTracks.updateState(tracks);
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.setTracks(tracks);
    });
  };
  const setShouldHandlePanics = (handle: boolean) => {
    debug('setShouldHandlePanics', handle);
    shouldHandlePanics = handle;
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      remotePeer.setShouldHandlePanics(handle);
    });
  };

  const updateConfig = (newConfig: Partial<MakeHostPeerConfig>) => {
    debug('updateConfig', newConfig);
    // Update the config object with new values
    Object.assign(config, newConfig);
  };

  const close = (kickReason?: string) => {
    debug('close host', kickReason);
    // disconnect all peers
    Object.values(remotePeers.currentState).forEach((remotePeer) => {
      manageKeyframeInterval.stopAllForPeer(remotePeer.remotePeerId);
      remotePeer.disconnect(false, kickReason ?? 'host:close');
    });
    // clean up state stores too
    status.reset(true);
    remotePeers.reset(true);
    defaultTracks.reset(true);
    qualityPresetStore.reset(true);
    statistics.reset(true);
  };

  const temporaryPeers: { [peerId: string]: PlaycastRemotePeerClients } = {};

  // Add a delay to clean up any peers that are disconnected but still in the state store
  // This is a hacky fix.
  setInterval(() => {
    const peerIds = Object.keys(remotePeers.currentState);
    peerIds.forEach((peerId) => {
      const remotePeer = remotePeers.currentState[peerId];
      if (remotePeer.status.currentState.connectionState === 'closed' || remotePeer.status.currentState.connectionState === 'refused') {
        delete remotePeers.currentState[peerId];
        remotePeers.updateState(remotePeers.currentState);
        console.error("Had to clean up a peer that wasn't removed properly", peerId);
      }
    });
  }, 4000);

  const onSignal = (peerId: string, sdp: RTCSessionDescriptionInit) => {
    debug('signaller.onSignal', peerId, sdp);
    const remotePeer = remotePeers.currentState[peerId];
    if (remotePeer) {
      remotePeer.signal(sdp);
    } else {
      const newPeer = createRemotePeer(hostPeerId, peerId, 'client', {
        signalServerConfig: config.signalServerConfig,
        realtimeApiManager: config.realtimeApiManager,
        iceServers: config.iceServers,
        forceTurn: config.forceTurn,
      });
      temporaryPeers[peerId] = newPeer;
      // Prime presets and default tracks BEFORE responding, so first O/A includes SSRCs and sendonly
      try {
        newPeer.applyQualityPresetGroup(qualityPresetStore.currentState);
      } catch (e) {
        // noop
      }
      if (Object.keys(defaultTracks.currentState).length > 0) {
        try {
          newPeer.setTracks(defaultTracks.currentState);
        } catch (e) {
          // noop
        }
      }
      newPeer.signal(sdp);
      // once the new peer has settled, set the default tracks
      const statusListener = newPeer.status.addListener(async (status) => {
        debug('newPeer.status', status);
        if (status.connectionState === 'validating') {
          // Validate the peer
          if (config.validatorFunction) {
            const isValid = await config.validatorFunction(peer, newPeer);
            if (!isValid) {
              newPeer.refuseConnection();
            } else {
              const validMessage = generators.peer.handshakeReply('host', hostPeerId, true);
              newPeer.sendMessage(validMessage, true);
              newPeer.status.updateState({ connectionState: 'connected' });
              delete temporaryPeers[peerId];
            }
          } else {
            const validMessage = generators.peer.handshakeReply('host', hostPeerId, true);
            newPeer.sendMessage(validMessage, true);
            newPeer.status.updateState({ connectionState: 'connected' });
            delete temporaryPeers[peerId];
          }
        }
        if (status.connectionState === 'connected') {
          newPeer.status.removeListener(statusListener);
          remotePeers.updateState({
            ...remotePeers.currentState,
            [peerId]: newPeer,
          });
          const messages = getNotifyMessages(['player']);
          messages.player.forEach((message) => {
            console.log('Sending message to player', message);
            newPeer.sendMessage(message);
          });
          newPeer.setShouldHandlePanics(shouldHandlePanics);
          // if the default tracks have been set, apply them but only if the peer has no tracks
          // use a timeout to ensure the peer has settled
          newPeer.applyQualityPresetGroup(qualityPresetStore.currentState);
          const outgoingTracks = newPeer.outgoingTracks.currentState;
          if (Object.keys(outgoingTracks).length === 0) {
            if (Object.keys(defaultTracks.currentState).length > 0) {
              newPeer.setTracks(defaultTracks.currentState);
            }
          }
          setTimeout(() => {
            // Ensure we set the presets after the tracks have been set
            newPeer.applyQualityPresetGroup(qualityPresetStore.currentState);
          }, 500);
          delete temporaryPeers[peerId];
        }
        if (status.connectionState === 'closed' || status.connectionState === 'refused') {
          newPeer.status.removeListener(statusListener);
          if (status.connectionState === 'closed') {
            log('Remote peer disconnected');
            newPeer.disconnect(false, 'host:status:close');
          } else {
            log('Remote peer refused');
            newPeer.refuseConnection();
          }
        }
      });

      const disconnectedListener = newPeer.status.addListener((status) => {
        if (status.connectionState === 'closed' || status.connectionState === 'refused') {
          delete remotePeers.currentState[peerId];
          delete temporaryPeers[peerId];
          newPeer.status.removeListener(disconnectedListener);
          remotePeers.updateState(remotePeers.currentState);
        }
      });
    }
  };
  const onCandidate = (peerId: string, candidate: RTCIceCandidate) => {
    debug('signaller.onCandidate', peerId, candidate);
    let remotePeer = remotePeers.currentState[peerId];
    if (!remotePeer) {
      remotePeer = temporaryPeers[peerId];
    }
    if (remotePeer) {
      if (USE_UDP_ONLY) {
        try {
          const proto = (candidate as unknown as { protocol?: string })?.protocol;
          if (proto && proto.toLowerCase() !== 'udp') return; // drop TCP
          const candStr: string = (candidate as any).candidate ?? '';
          if (candStr && !candStr.includes(' udp ')) return; // drop TCP
        } catch {
          // ignore parse errors
        }
      }
      remotePeer.addCandidate(candidate);
    } else {
      warn('Received ICE candidate for unknown peer', peerId);
    }
  };

  const handlePeerMessage = (message: PeerMessage) => {
    switch (message.type) {
      case PeerMessageType.PEER_SDP_ANSWER:
      case PeerMessageType.PEER_SDP_OFFER: {
        if (message.toId !== hostPeerId) {
          warn('Received SDP message for unknown peer', message.toId);
          return;
        }
        if (message.sdp?.type && message.sdp?.sdp) {
          const sdpObj: RTCSessionDescriptionInit = {
            type: message.sdp.type,
            sdp: message.sdp.sdp,
          };
          onSignal(message.fromId, sdpObj);
        } else {
          warn('Invalid SDP message received:', message.sdp);
        }
        break;
      }
      case PeerMessageType.PEER_ICE_CANDIDATE: {
        if (message.toId !== hostPeerId) {
          warn('Received ICE candidate for unknown peer', message.toId);
          return;
        }
        try {
          for (const candidate of message.candidates.json) {
            onCandidate(message.fromId, JSON.parse(candidate));
          }
        } catch (e) {
          warn('Failed to parse ICE candidate', e);
        }
        break;
      }
    }
  };

  const peer: PlaycastLocalHosts = {
    peerId: hostPeerId,
    type,
    metadata: {
      ...config.additionalMetadata,
      peerId: hostPeerId,
      type,
    },
    status,
    remotePeers,
    defaultTracks,
    qualityPresetStore,
    changeQualityProfile,
    setQualityPreset,
    setQualityPresetGroup,
    handlePeerMessage,
    broadcastMessage,
    addTracksForAll,
    removeTracksForAll,
    setTracksForAll,
    setDefaultTracks,
    setShouldHandlePanics,
    close,
    updateConfig,
  };

  let cachedPeerCount = 0;
  remotePeers.addListener((remotePeers) => {
    const filteredPeers = Object.values(remotePeers).map((peer) => {
      return {
        remotePeerId: peer.remotePeerId,
        type: peer.type,
        connectionState: peer.status.currentState.connectionState,
      };
    });
    if (filteredPeers.length === 0) {
      filteredPeers.push({
        remotePeerId: 'No remote peers connected',
        type: 'unknown',
        connectionState: 'unknown',
      } as never);
    }
    const newPeerCount = Object.keys(remotePeers).length;
    if (cachedPeerCount !== newPeerCount) {
      if (cachedPeerCount > newPeerCount) log('A remote peer disconnected');
      if (cachedPeerCount < newPeerCount) log('A remote peer connected');
      cachedPeerCount = newPeerCount;
      console.table(filteredPeers, ['remotePeerId', 'type', 'connectionState']);
    }
  });

  status.updateState({ connectionState: 'listening' });

  return peer;
};
